import { useState, useMemo, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { ToggleButtonGroup, ToggleButton } from "react-bootstrap"

import "./index.scss"

import ScheduleElementHour from "./ScheduleElementHour"
import ScheduleAction from "./ScheduleAction"
import ScheduleWhiteTask from "./ScheduleWhiteTask"
import ScheduleAnalysisTask from "./ScheduleAnalysisTask"
import ScheduleItem from "./ScheduleItem"
import { Popup } from "components"

import { ActionConfig, WhiteTaskConfig, AnalysisTaskConfig } from "components/Config"
import { SampleTableForPlannedAnalysis } from "components/SampleTable"

import { analysisTasks, analyses, projectActions, projects, whiteTasks, visual } from "state_management"

import { buildHours, getWidth } from "utils/scripts/schedule"
import { DateTime } from "luxon"
import isOnMobile from "utils/scripts/isOnMobile"
import useMemoizeArray from "utils/scripts/useMemoizeArray"
import useT from "utils/hooks/useT"

// The schedule items / hours on a single member, and a single date.
export default function ScheduleElement({
  memberId,
  minMemberHeight,
  maxMemberHeight,
  date,
  extended,
  view,
  isAltPressed,
  isMobile,
  screen = "teamPlan" // Default to teamPlan for backwards compatibility
}) {
  const { t } = useT("common")
  const dispatch = useDispatch()
  const allWhiteTasks = useSelector((state) => state.whiteTasks)
  const allAnalysisTasks = useSelector((state) => state.analysisTasks)
  const allProjectActions = useSelector((state) => state.projectActions.plannedProjectActions)
  const allProjects = useSelector((state) => state.projects)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const isCompactView = useSelector((state) => state.visual.screens[screen]?.showCompact ?? state.visual.showCompact)
  const scheduleMondayDate = useSelector((state) => state.visual.scheduleMondayDate)
  const scheduleWithWeek = useSelector((state) => state.visual.screens[screen]?.scheduleWithWeek ?? state.visual.scheduleWithWeek)
  // Programmatic open-analysis-task request (PRD-003 Area 1 / SPEC-007): when this matches a task
  // rendered on this member+date, open its config exactly like a click, then clear the flag.
  const requestedAnalysisTaskId = useSelector((state) => state.visual.activeAnalysisTaskId)
  // SPEC-022 (K2): when the programmatic open comes from a LIMS work context, land on the Edit
  // Analysis-task view with the "QC Results & Approvals" section expanded + scrolled into view.
  const requestedFocusQc = useSelector((state) => state.visual.analysisTaskFocusQc)

  const [activeActionId, setActiveActionId] = useState(null)
  const activeAction = useSelector(
    (state) => (activeActionId && state.projectActions.plannedProjectActions[activeActionId]) || null
  )
  const [activeWhiteTaskId, setActiveWhiteTaskId] = useState(null)
  const activeWhiteTask = useSelector((state) => (activeWhiteTaskId && state.whiteTasks[activeWhiteTaskId]) || null)
  const [activeAnalysisTaskId, setActiveAnalysisTaskId] = useState(null)
  const liveAnalysisTask = useSelector(
    (state) => (activeAnalysisTaskId && state.analysisTasks[activeAnalysisTaskId]) || null
  )
  // SPEC-028 (RR4 #4): keep the opened analysis modal alive until the user explicitly closes it. A
  // background refetch (loading.fetchAll → analyses.fetchAndReplace REBUILDS analysisTasks) can drop
  // the open task from state mid-work — e.g. once its results go to QA and it leaves the current plan
  // view — which previously unmounted the modal and dumped the user back on the plan. We capture the
  // task when it's opened (the two open paths below) and fall back to that snapshot if the live task
  // disappears; the modal's visibility is governed by activeAnalysisTaskId, so an explicit close hides
  // it regardless and the snapshot is simply refreshed on the next open.
  const [analysisTaskSnapshot, setAnalysisTaskSnapshot] = useState(null)
  const activeAnalysisTask = liveAnalysisTask || analysisTaskSnapshot
  const [activeSamplePopupAnalysisId, setActiveSamplePopupAnalysisId] = useState(null)
  // SPEC-022 (K2): true while the open task config should focus the QC section (captured from the
  // redux focusQc flag before it is cleared). Reset on every normal click-open.
  const [focusQcSection, setFocusQcSection] = useState(false)
  const [bookedAndFreeAssets, setBookedAndFreeAssets] = useState({bookedAssets: [], freeAssets: []})
  const [activeExternalBooking, setActiveExternalBooking] = useState(null)

  const allExternalBookings = useSelector((state) => state.externalBookings)
  const externalBookingsOnMember = useMemo(() =>
    (allExternalBookings || []).filter((b) => b.memberId === memberId),
    [allExternalBookings, memberId]
  )

  const externalBookingItems = useMemo(() =>
    externalBookingsOnMember.map((booking) => ({
      id: `ext-${booking.memberId}-${booking.start}`,
      memberIds: [booking.memberId],
      __typename: "WhiteTask",
      start: booking.start,
      end: booking.end,
      name: booking.name,
      description: booking.description || "",
      movable: booking.movable,
      teamName: booking.teamName || null,
      fromOutlook: true,
      isExternalBooking: true,
    })),
    [externalBookingsOnMember]
  )

  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const isMember = useSelector((state) => state.auth.isMember)
  const allowTaskTransfer = useSelector((state) => state.teamStatus.allowTaskTransfer)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const currentMemberShowOnPlan = useSelector((state) => authMemberId && state.members[authMemberId] ? state.members[authMemberId].showOnPlan : true)
  const isReadOnly = isViewOnly || (isMember && !allowTaskTransfer && (!currentMemberShowOnPlan || memberId !== authMemberId))
  const isDayView = view === "day-view"

  const scheduleMondayDateTime = DateTime.fromISO(scheduleMondayDate);

  const startOfWeek = scheduleMondayDateTime.startOf('week');
  const endOfWeek = scheduleWithWeek ? startOfWeek.endOf('week').plus({ weeks: 1 }) : startOfWeek.endOf('week');

  const startOfWeekISO = startOfWeek.toISO();
  const endOfWeekISO = endOfWeek.toISO();

  useEffect(() => {
    if (activeActionId && !activeAction) {
      setActiveActionId(null)
    }
  }, [activeActionId, activeAction])


  const tasksInCurrentWeek = useMemo(() => {
    const isInCurrentWeek = ({ start, end }) => {
      return start <= endOfWeekISO && end >= startOfWeekISO;
    };
    const filterTasks = (tasks) => Object.values(tasks).filter(isInCurrentWeek);
    return {
      whiteTasks: filterTasks(allWhiteTasks),
      analysisTasks: filterTasks(allAnalysisTasks),
      projectActions: filterTasks(allProjectActions),
    };
  }, [allWhiteTasks, allAnalysisTasks, allProjectActions, endOfWeekISO, startOfWeekISO]);

// Use tasksInCurrentWeek.whiteTasks, etc., where appropriate

  // This is memoized, so `getWidth` is run as little as possible, even if the
  // tasks / actions change.
  const itemsOnThisDate = useMemoizeArray(
    useMemo(() => {
      const startOfDate = DateTime.fromISO(date).setZone(teamTimeZone).startOf("day")
      const endOfDate = startOfDate.endOf("day")
      return [...tasksInCurrentWeek.whiteTasks, ...tasksInCurrentWeek.analysisTasks, ...tasksInCurrentWeek.projectActions, ...externalBookingItems]
        .filter(({ memberIds }) => memberIds && memberIds.indexOf(memberId) !== -1)
        .filter(({ start }) => DateTime.fromISO(start).setZone(teamTimeZone) <= endOfDate)
        .filter(({ end }) => DateTime.fromISO(end).setZone(teamTimeZone) > startOfDate)
        .filter(({ greenTask }) => !greenTask)
        .sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)) // Consistent order, see useMemoizeArray
    }, [tasksInCurrentWeek, externalBookingItems, date, memberId, teamTimeZone])
  )

  // Honour a programmatic open-analysis-task request (PRD-003 Area 1 / SPEC-007). Only the
  // ScheduleElement that actually renders the requested task (this member + date) reacts; it opens
  // the task's config exactly like a click and clears the redux flag so it fires once. Used to land
  // a reviewer on the analysis from the Reviews panel / planned review tile. SPEC-022 (K2): a
  // focusQc request (every LIMS work context) opens the Edit Analysis-task view directly with the
  // "QC Results & Approvals" section expanded; a plain request keeps the SAMPLE tab as before.
  useEffect(() => {
    if (!requestedAnalysisTaskId) return
    const rendered = itemsOnThisDate.some(
      (item) => item.__typename === "AnalysisTask" && item.id === requestedAnalysisTaskId
    )
    if (!rendered) return
    const task = allAnalysisTasks[requestedAnalysisTaskId]
    if (!task) return
    // Capture focusQc, then clear the redux flags first so this fires once; open the task's
    // config asynchronously (off the effect body) on the requested view.
    const focusQc = requestedFocusQc
    dispatch(visual.clearActiveAnalysisTask())
    const open = () => {
      setFocusQcSection(focusQc)
      setActiveAnalysisTaskId(requestedAnalysisTaskId)
      setAnalysisTaskSnapshot(task) // SPEC-028: snapshot so a background refetch can't unmount the open modal
      // focusQc → Edit Analysis-task view (AnalysisTaskConfig); otherwise the SAMPLE tab.
      setActiveSamplePopupAnalysisId(focusQc ? null : task.analysisId)
    }
    if (isReadOnly) {
      Promise.resolve().then(open)
    } else {
      dispatch(analysisTasks.getBookedFreeAssets(requestedAnalysisTaskId, task)).then((res) => {
        setBookedAndFreeAssets(res)
        open()
      })
    }
  }, [requestedAnalysisTaskId, requestedFocusQc, itemsOnThisDate, allAnalysisTasks, isReadOnly, dispatch])

  // This is used to distinguish green from white tasks
  const greenTasksMap = useMemo(() => {
    const rtn = {}
    const startOfDate = DateTime.fromISO(date).setZone(teamTimeZone).startOf("day")
    const endOfDate = startOfDate.endOf("day")
    tasksInCurrentWeek.whiteTasks
      .filter(({ memberIds }) => memberIds && memberIds.indexOf(memberId) !== -1)
      .filter(({ start }) => DateTime.fromISO(start).setZone(teamTimeZone) <= endOfDate)
      .filter(({ end }) => DateTime.fromISO(end).setZone(teamTimeZone) > startOfDate)
      .filter(({ greenTask, __typename }) => __typename === "WhiteTask" && greenTask)
      .forEach((task) => {
        const start = DateTime.fromISO(task.start).setZone(teamTimeZone)
        const end = DateTime.fromISO(task.end).setZone(teamTimeZone)
        const endIsOtherDateThanStart = start.startOf("day") < end.startOf("day")
        if (endIsOtherDateThanStart && end.day !== DateTime.fromISO(date).setZone(teamTimeZone).day) {
          for (let i = start.hour; i < 24; i += extended ? 0.25 : 1) {
            rtn[i] = task
          }
        } else if (endIsOtherDateThanStart && end.day === DateTime.fromISO(date).setZone(teamTimeZone).day) {
          for (let i = 0; i < end.hour; i += extended ? 0.25 : 1) {
            rtn[i] = task
          }
        } else {
          for (let i = start.hour; i < end.hour; i += extended ? 0.25 : 1) {
            rtn[i] = task
          }
        }
      })
    return rtn
  }, [tasksInCurrentWeek.whiteTasks, extended, date, memberId, teamTimeZone])

  const taskDescriptions = useMemo(() => {
    const startOfDate = DateTime.fromISO(date).setZone(teamTimeZone).startOf("day")
    const endOfDate = startOfDate.endOf("day")
    const itemWithShouldShowState = itemsOnThisDate.map((item) => {
      const start = DateTime.fromISO(item.start).setZone(teamTimeZone)
      const end = DateTime.fromISO(item.end).setZone(teamTimeZone)
      if (start >= startOfDate && end <= endOfDate) {
        return { ...item, shouldShowState: 1 }
      } else if (start >= startOfDate) {
        return { ...item, end: endOfDate.toISO(), shouldShowState: 2 }
      } else if (end <= endOfDate) {
        return { ...item, start: startOfDate.toISO(), shouldShowState: 3 }
      } else {
        return { ...item, start: startOfDate.toISO(), end: endOfDate.toISO(), shouldShowState: 4 }
      }
    })

    return getWidth(startOfDate, itemWithShouldShowState, 24 * 60)
  }, [itemsOnThisDate, date, teamTimeZone])

  const renderedTasks = useMemo(() => {
    const minHeight = extended ? 1.25 : Math.min(5, (0.5 / (maxMemberHeight - minMemberHeight)) * 100)
    let daySize = maxMemberHeight - minMemberHeight
    if (extended) {
      daySize = daySize * 4
    }
    return taskDescriptions.map(({ width, left, obj }) => {
      const absoluteHeight = Math.floor(DateTime.fromISO(obj.end).setZone(teamTimeZone).diff(DateTime.fromISO(obj.start).setZone(teamTimeZone)).as("minutes")) / 60
      const h = (absoluteHeight / (maxMemberHeight - minMemberHeight)) * 100
      const absoluteDist =
        Math.floor(DateTime.fromISO(obj.start).setZone(teamTimeZone).hour) +
        Math.ceil(DateTime.fromISO(obj.start).setZone(teamTimeZone).minute) / 60 -
        minMemberHeight
      const top = (absoluteDist / (maxMemberHeight - minMemberHeight)) * 100
      const style = {
        position: "absolute",
        width: width + "%",
        left: left + "%",
        height: isMobile ? 28 * absoluteHeight * 4 + "px" : h + "%",
        top: top + "%",
        minHeight: minHeight + "%",
        fontSize: isCompactView && !extended ? "0.5rem" :
          Math.max(
            Math.min(((Math.max(minHeight, h) / 100) * daySize) / 1.1, 1.1),
            isDayView ? (h / 7 > 1.5 ? 1.5 : 0.6) : 0.6
          ) + "rem",
      }

      if (obj.__typename === "ProjectAction") {
        return (
          <ScheduleAction
            key={obj.id + " action"}
            actionId={obj.id}
            shouldShowState={obj.shouldShowState}
            style={style}
            onClick={() => {
              if (!isReadOnly) {
                dispatch(projectActions.getBookedFreeAssets(obj.id, allProjectActions[obj.id])).then((res) => {
                  setBookedAndFreeAssets(res)
                })
              }
              setActiveActionId(obj.id)
            }}
            showIcons={!isCompactView || extended}
          />
        )
      } else if (obj.__typename === "AnalysisTask") {
        const shouldShowSampleCount = ((!extended && absoluteHeight >= 2.1) || (extended && absoluteHeight >= 0.55)) && !isCompactView
        return (
          <ScheduleAnalysisTask
            key={obj.id + " analysis_task"}
            taskId={obj.id}
            shouldShowState={obj.shouldShowState}
            shouldShowSampleCount={shouldShowSampleCount}
            style={style}
            memberId={memberId}
            onClick={() => {
              if (!isReadOnly) {
                dispatch(analysisTasks.getBookedFreeAssets(obj.id, allAnalysisTasks[obj.id])).then((res) => {
                  setBookedAndFreeAssets(res)
                })
              }
              setFocusQcSection(false) // normal click-open: no QC-section focus (SPEC-022 K2)
              setActiveAnalysisTaskId(obj.id)
              setAnalysisTaskSnapshot(allAnalysisTasks[obj.id]) // SPEC-028: snapshot so a background refetch can't unmount the open modal
              setActiveSamplePopupAnalysisId(allAnalysisTasks[obj.id].analysisId)
            }}
            isAltPressed={isAltPressed}
            showIcons={!isCompactView || extended}
          />
        )
      } else if (obj.isExternalBooking) {
        return (
          <ScheduleItem
            key={obj.id}
            item={obj}
            backgroundColor="#000000"
            shouldShowState={obj.shouldShowState}
            name={obj.name}
            description={obj.description}
            movable={obj.movable}
            style={style}
            onClick={() => setActiveExternalBooking(obj)}
          />
        )
      } else if (!obj.greenTask) {
        return (
          <ScheduleWhiteTask
            key={obj.id + " white_task"}
            taskId={obj.id}
            shouldShowState={obj.shouldShowState}
            style={style}
            onClick={() => {
              if (!isReadOnly && obj.recurrenceInterval === null) {
                dispatch(whiteTasks.getBookedFreeAssets(obj.id, allWhiteTasks[obj.id])).then((res) => {
                  setBookedAndFreeAssets(res)
                })
              } else {
                setBookedAndFreeAssets({bookedAssets: [], freeAssets: []})
              }
              setActiveWhiteTaskId(obj.id)
            }}
          />
        )
      } else {
        return <></>
      }
    })
  }, [taskDescriptions, extended, minMemberHeight, maxMemberHeight, isDayView, isMobile, memberId,
      teamTimeZone, allAnalysisTasks, allProjectActions, allWhiteTasks, dispatch, isAltPressed, isCompactView, isReadOnly])

  const renderedHours = useMemo(() => {
    const day = DateTime.fromISO(date).setZone(teamTimeZone);
    let hours = buildHours(extended, minMemberHeight, maxMemberHeight);

    if (isCompactView) {
      const compactStepSize = extended ? 1 : 2; // Goes back to normal extended view
      let filteredHours = [];

      for (let i = 0; i < hours.length; i += compactStepSize) {
        filteredHours.push(hours[i]);
      }

      // Ensure the last hour is included, checking for unique last hour
      const lastHour = hours[hours.length - 1];
      if (!filteredHours.includes(lastHour)) {
        filteredHours.push(lastHour);
      }

      hours = filteredHours;
    }

    return hours.map((hour) => (
      <ScheduleElementHour
        memberId={memberId}
        date={day.startOf("day").plus({ hours: hour }).toISO()}
        interval={extended ? 0.25 : 1}
        key={hour}
        task={greenTasksMap[hour]}
        isMobile={isMobile}
      />
    ));
  }, [extended, minMemberHeight, maxMemberHeight, date, memberId, greenTasksMap, isMobile, teamTimeZone, isCompactView]);

  return (
    <>
      {activeWhiteTask && (
        <Popup onCancel={() => setActiveWhiteTaskId(null)}>
          <WhiteTaskConfig task={activeWhiteTask} onClose={() => setActiveWhiteTaskId(null)} type="team-plan" bookedAndFreeAssets={bookedAndFreeAssets} disabled={isReadOnly}/>
        </Popup>
      )}
      {activeExternalBooking && (
        <Popup onCancel={() => setActiveExternalBooking(null)}>
          <WhiteTaskConfig task={activeExternalBooking} onClose={() => setActiveExternalBooking(null)} type="team-plan" bookedAndFreeAssets={{bookedAssets: [], freeAssets: []}} disabled={true}/>
        </Popup>
      )}
      {activeAction && (
        <Popup className="Edit-Create-Project" onCancel={() => setActiveActionId(null)}>
          <ActionConfig
            title={t("editAction")}
            action={activeAction}
            isSingleAction={
              activeAction &&
              allProjects[activeAction.projectId] &&
              allProjects[activeAction.projectId].singleActionProject
            }
            cancelHandler={() => setActiveActionId(null)}
            submitHandler={(res) =>
              dispatch(projectActions.editPlanned(activeActionId, res)).then(() => setActiveActionId(null))
            }
            deleteTitle={t("unplan")}
            deleteHandler={() => {
              setActiveActionId(null)
              dispatch(projectActions.removePlanned(activeActionId))
            }}
            shiftDeleteHandler={() => {
              const projectId = allProjects[allProjectActions[activeActionId].projectId].id
              dispatch(projects.unplan(projectId)).then(() => setActiveActionId(null))
            }}
            projectId={!isOnMobile ? activeAction.projectId : null}
            confirmDeletionText={t("areYouSureUnplanAllFutureActions")}
            bookedAndFreeAssets={bookedAndFreeAssets}
            disabled={isReadOnly}
          />
        </Popup>
      )}
      {activeAnalysisTaskId && activeAnalysisTask && (
        <Popup onCancel={() => {
          setActiveAnalysisTaskId(null)
          setActiveSamplePopupAnalysisId(null)
          setFocusQcSection(false)
          setBookedAndFreeAssets({bookedAssets: [], freeAssets: []})
          // Reset activeSamplePopupAnalysisId, to avoid that it carries over to another analysis
        }}
        title={
          <div className="taskTabs">
            {/* Controlled by activeSamplePopupAnalysisId (set = SAMPLE view, null = TASK view) so a
                programmatic focusQc open (SPEC-022 K2) highlights the Edit Analysis-task tab. */}
            <ToggleButtonGroup
              type="radio"
              name="taskTabs_name"
              value={activeSamplePopupAnalysisId ? "SAMPLE" : "TASK"}
            >
              <ToggleButton
                title={t("viewAnalysisSamples")}
                variant="primary"
                value="SAMPLE"
                id="analysis-sample-view_id"
                className="task-tab-btn"
                onChange={() => setActiveSamplePopupAnalysisId(activeAnalysisTask.analysisId)}
              >
                {t("viewAnalysisSamples")}
              </ToggleButton>
              <ToggleButton
                title={t("editAnalysisTask")}
                variant="primary"
                value="TASK"
                id="analysis-task-view_id"
                className="task-tab-btn"
                onChange={() => setActiveSamplePopupAnalysisId(null)}
              >
                {t("editAnalysisTask")}
              </ToggleButton>
            </ToggleButtonGroup>
          </div>
          }
        >
            {!activeSamplePopupAnalysisId && (
              <AnalysisTaskConfig
                task={activeAnalysisTask}
                focusQcSection={focusQcSection}
                cancelHandler={() => {
                  setActiveAnalysisTaskId(null)
                  setFocusQcSection(false)
                  setBookedAndFreeAssets({bookedAssets: [], freeAssets: []})
                }}
                submitHandler={(res) =>
                  dispatch(analysisTasks.edit(activeAnalysisTaskId, res)).then(() => {
                    setActiveAnalysisTaskId(null)
                    setBookedAndFreeAssets({bookedAssets: [], freeAssets: []})
                    if (activeAnalysisTask.analysisId) {
                      dispatch(analyses.setMovable(activeAnalysisTask.analysisId))
                    }
                  })
                }
                deleteHandler={() => {
                  dispatch(analyses.remove(activeAnalysisTask.analysisId)).then(() => {
                    setActiveAnalysisTaskId(null)
                    setBookedAndFreeAssets({bookedAssets: [], freeAssets: []})
                  })
                }}
                bookedAndFreeAssets={bookedAndFreeAssets}
                disabled={isReadOnly}
              />
            )}
            {activeSamplePopupAnalysisId && <SampleTableForPlannedAnalysis analysisId={activeSamplePopupAnalysisId} cancelHandler={() => {
              setActiveAnalysisTaskId(null)
              setActiveSamplePopupAnalysisId(null)
              // Reset activeSamplePopupAnalysisId, to avoid that it carries over to another analysis
            }} readOnly={isReadOnly} />}
        </Popup>
      )}
      <div className="ScheduleElement-container">
        {renderedHours}
        {renderedTasks}
      </div>
    </>
  )
}
